import Keycloak from "keycloak-js"
import { clearSession } from "@/auth/session"

// keycloak-js 26 calls Promise.withResolvers() on the token-refresh path. It
// ships untranspiled and node_modules is excluded from Babel, so core-js never
// polyfills it. Without this shim every refresh throws on Chrome <119 /
// Safari <17.4 / Firefox <121, and the session can never be renewed.
if (typeof Promise.withResolvers !== "function") {
  Promise.withResolvers = function () {
    let resolve
    let reject

    const promise = new Promise((res, rej) => {
      resolve = res
      reject = rej
    })

    return { promise, resolve, reject }
  }
}

// Browsers only expose crypto.randomUUID on secure origins (HTTPS/localhost).
// front.test over HTTP is not secure, so polyfill it for local development.
if (process.env.NODE_ENV === "development" && typeof crypto !== "undefined" && !crypto.randomUUID) {
  crypto.randomUUID = () =>
    "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    )
}

// Single Keycloak instance for the whole app. The access/refresh tokens live
// in-memory on this instance and are never persisted to storage.
const keycloak = new Keycloak({
  url: process.env.VUE_APP_KEYCLOAK_URL,
  realm: process.env.VUE_APP_KEYCLOAK_REALM,
  clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
})

// Current access token, or undefined before authentication completes.
export const getToken = () => keycloak.token

export const login = (options) => keycloak.login(options)

// Force a token refresh regardless of remaining validity (used by the API
// client to recover from a 401 before giving up).
export const forceRefresh = () => keycloak.updateToken(-1)

// Seconds of remaining validity below which a request-time refresh kicks in.
const REFRESH_MIN_VALIDITY_SECONDS = 30

// Refresh only if the token is about to expire. Cheap enough to call on every
// request: keycloak-js returns immediately without touching the network while
// the token is still valid, and coalesces concurrent refreshes into one call.
export const refreshIfExpiring = () => keycloak.updateToken(REFRESH_MIN_VALIDITY_SECONDS)

// Clear local identity/credentials, then redirect to the Keycloak logout
// endpoint. Use this everywhere instead of calling keycloak.logout() directly
// so storage cleanup happens on every logout path.
let loggingOut = false

export const logout = (options = {}) => {
  // logout() navigates away, so a second call — a double-clicked button, two
  // callers racing — would start a competing navigation and the winner would be
  // whichever assignment to window.location happened to land last.
  if (loggingOut) {
    return Promise.resolve()
  }

  loggingOut = true

  clearSession()

  // Release the latch if the redirect never happens, otherwise a failed logout
  // would leave the Sign out button permanently inert.
  return keycloak.logout({ redirectUri: process.env.VUE_APP_BASE_URL, ...options }).catch((e) => {
    loggingOut = false
    throw e
  })
}

// Wire the lifecycle callbacks and start Keycloak. `onReady(authenticated)` is
// the app-specific boot handler (fetch user, mount Vue) and runs once init
// resolves. Returns the init() promise so the caller can handle init failure.
export const initAuth = (onReady) => {
  keycloak.onReady = onReady

  // Clear local identity, but never navigate from here. Both paths that can
  // reach onAuthError in this app (a callback carrying an error, and a failed
  // code-for-token exchange) throw immediately afterwards, so init() rejects and
  // main.js renders the boot error screen with the cause on it. Redirecting to
  // logout instead would preempt that screen and then bounce straight off
  // "login-required" back to Keycloak, reproducing the same error on every pass
  // — a redirect loop with nothing shown to the user.
  keycloak.onAuthError = (error) => {
    console.error("[AUTH] Authentication failed", error)
    clearSession()
  }

  // No onAuthRefreshError handler, deliberately — both outcomes are already
  // handled correctly without one.
  //
  // If a token survived the failure the refresh broke transiently (dropped
  // connection, 5xx from the proxy in front of Keycloak) and the session is
  // still usable, so it must not be torn down — that would cost the user their
  // unsaved work for nothing. The next request retries via the toolsClient 401
  // handler.
  //
  // If the token is gone the refresh was genuinely rejected (400
  // invalid_grant), and keycloak-js has already cleared storage through
  // onAuthLogout and redirected to the login page itself, because onLoad is
  // "login-required". Calling logout() here would race that navigation — and
  // race it having already discarded the id_token, so the logout request would
  // carry no id_token_hint and Keycloak would answer it with a "Do you want to
  // log out?" confirmation page instead of the login form.

  keycloak.onAuthLogout = () => clearSession()

  keycloak.onTokenExpired = async () => {
    try {
      await keycloak.updateToken(60)
    } catch (e) {
      // Nothing to decide here. A genuine rejection has already been handled
      // by keycloak-js (see above); a transient one just leaves the token
      // expired for the toolsClient 401 retry to recover.
      console.error("[AUTH] Token refresh failed", e)
    }
  }

  return keycloak.init({
    onLoad: "login-required",
    checkLoginIframe: false,
    ...(process.env.NODE_ENV === "development" ? { pkceMethod: false } : {}),
  })
}

export default keycloak
